const BASE = import.meta.env.VITE_API_URL ?? '/v1';

export class ApiError extends Error {
  constructor(status, code, message) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

async function request(method, path, body, opts = {}) {
  // Ne set Content-Type: application/json QUE s'il y a un body à envoyer.
  // Sinon Fastify rejette avec FST_ERR_CTP_EMPTY_JSON_BODY.
  const headers = { ...(opts.headers ?? {}) };
  const init = {
    method,
    credentials: 'include',
    headers,
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  const res = await fetch(`${BASE}${path}`, init);

  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    throw new ApiError(401, 'UNAUTHORIZED', 'Session expirée');
  }

  if (res.status === 204) return null;

  let json;
  try {
    json = await res.json();
  } catch {
    throw new ApiError(res.status, 'PARSE_ERROR', 'Réponse invalide du serveur');
  }

  if (!res.ok) {
    // Backend format: { error: { code, message } }
    const errObj = json?.error ?? json;
    const code = errObj?.code ?? 'API_ERROR';
    const message = errObj?.message ?? json?.message ?? 'Erreur serveur';
    throw new ApiError(res.status, code, message);
  }

  return json;
}

// Upload multipart (FormData) — ne PAS fixer Content-Type, le navigateur pose la boundary.
async function upload(path, formData, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method: opts.method ?? 'POST',
    credentials: 'include',
    headers: { ...(opts.headers ?? {}) },
    body: formData,
  });

  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent('auth:unauthorized'));
    throw new ApiError(401, 'UNAUTHORIZED', 'Session expirée');
  }
  if (res.status === 204) return null;

  let json;
  try {
    json = await res.json();
  } catch {
    throw new ApiError(res.status, 'PARSE_ERROR', 'Réponse invalide du serveur');
  }
  if (!res.ok) {
    const errObj = json?.error ?? json;
    throw new ApiError(res.status, errObj?.code ?? 'API_ERROR', errObj?.message ?? 'Erreur serveur');
  }
  return json;
}

/** URL absolue vers une route backend — pour les <img src>/<a href> chargés par le navigateur. */
export function apiUrl(path) {
  return `${BASE}${path}`;
}

const api = {
  get: (path, opts) => request('GET', path, undefined, opts),
  post: (path, body, opts) => request('POST', path, body, opts),
  patch: (path, body, opts) => request('PATCH', path, body, opts),
  put: (path, body, opts) => request('PUT', path, body, opts),
  delete: (path, opts) => request('DELETE', path, undefined, opts),
  // DELETE avec body JSON (non standard mais supporté par Fastify)
  deleteWithBody: (path, body, opts) => request('DELETE', path, body, opts),
  upload,
};

export default api;
